// chatContext/deriveAppContextEnvelope.ts
//
// The whole app-context envelope, derived in one pure function so it can be
// tested per route, per activity and per anchor kind without a React tree.
// `useAppContextEnvelope` is the only production caller; it supplies the inputs
// from the router, the study slices and the surface-published anchor.

import { StudyPhase } from '../slices/study/studySlice';
import {
  CHAT_PREFILL_MAX_CHARS,
  CHAT_SELECTION_MAX_CHARS,
  CHAT_VIDEO_TIMESTAMP_BUCKET_SECONDS,
  type ChatActivity,
  type ChatAnchorInput,
  type ChatAnchorRef,
  type ChatAppContextEnvelope,
  type ChatFlashcardScope,
  type ChatRouteId,
  type ChatSurface,
  type ChatCourseContext,
  type ChatViewing,
} from '../types/chatEnvelope';
import { chatContextKey } from './chatContextKey';
import type { ChatBlockContext } from './chatBlockThread';

/** The QBank block the student is in, flattened out of the blocks slice. */
export interface AppContextActiveBlock {
  id: string;
  vignetteCount: number;
  isSubmitted: boolean;
  /** Set when this block belongs to a self-assessment attempt. */
  selfAssessmentAttemptId: string | null;
  /** True once the attempt is finished and open for review. */
  selfAssessmentIsReviewable: boolean;
}

/** The flashcard run the student is in, flattened out of the run slice. */
export interface AppContextFlashcardRun {
  scope: ChatFlashcardScope | null;
  completedCount: number;
  totalCount: number;
}

/** Everything the envelope is derived from. */
export interface AppContextEnvelopeInput {
  surface: ChatSurface;
  route: ChatRouteId;
  /** The browser path; only the Help route puts it on the envelope. */
  path?: string | null;
  anchor: ChatAnchorInput | null;
  /**
   * The context key the student removed with the composer's pill (ORA-662).
   * When it matches what is on screen the envelope says so and
   * `withScreenSnapshot` withholds the page text.
   */
  detachedContextKey?: string | null;
  selection: string | null;
  prefill: string | null;
  viewing?: ChatViewing | null;
  /** Where in a course the student is; context only. */
  course?: ChatCourseContext | null;
  /** The activity a course step published; read on `course.session` only. */
  courseActivity?: ChatActivity | null;
  study: {
    phase: StudyPhase;
    /** 0-based index the study slice keeps; the envelope reports it 1-based. */
    activeQuestionIndex: number;
    /**
     * The block the vignettes on screen were loaded for. This is what the study
     * route is actually showing; `activeBlock` is what the session or the
     * standalone context points at, and the two disagree while a block loads.
     */
    activeQuestionBlockId: string | null;
    activeBlock: AppContextActiveBlock | null;
    flashcardRun: AppContextFlashcardRun | null;
    todaySessionId: string | null;
  };
}

export interface AppContextEnvelopeResult {
  envelope: ChatAppContextEnvelope;
  /**
   * False while a self-assessment attempt is (or might still be) live. Every
   * chat entry point — drawer tab, study-panel tab, selection tooltip,
   * dashboard prompt — is hidden on this flag, and a turn that somehow escapes
   * the gate carries neither free text nor a revealed anchor.
   */
  isChatAvailable: boolean;
}

const QBANK_ROUTES: readonly ChatRouteId[] = [
  'study.qbank.block',
  'study.qbank.review',
  'self-assessment.block',
];

/** A block that belongs to an attempt whose correctness is still withheld. */
const isUnreviewableAttempt = (block: AppContextActiveBlock): boolean =>
  Boolean(block.selfAssessmentAttemptId) &&
  block.selfAssessmentIsReviewable !== true;

/**
 * Is a self-assessment attempt live, as far as we can prove?
 *
 * Every branch fails closed, because the cost of a wrong "no" is a student
 * asking the companion to answer a proctored question:
 *
 * - The dedicated block route is only ever entered inside an attempt (it is a
 *   spinner that redirects into the study route), so it hides chat outright.
 * - The attempt itself runs under the ordinary study route, where the only
 *   signal is the block. So an absent block, or one that is not the block the
 *   route is showing, counts as an attempt until proven otherwise.
 * - Everywhere else — review routes, the rest of the app — the block's own
 *   flags decide, matching the legacy gate in `AIDrawerRenderer`.
 */
const isSelfAssessmentLive = (
  block: AppContextActiveBlock | null,
  route: ChatRouteId,
  activeQuestionBlockId: string | null
): boolean => {
  if (route === 'self-assessment.block') return true;

  if (route === 'study.qbank.block') {
    if (!block) return true;
    if (activeQuestionBlockId !== block.id) return true;

    return isUnreviewableAttempt(block);
  }

  return block ? isUnreviewableAttempt(block) : false;
};

const capped = (value: string | null, max: number): string | undefined => {
  const trimmed = value?.trim();

  if (!trimmed) return undefined;
  if (trimmed.length <= max) return trimmed;

  const cut = trimmed.slice(0, max);
  const lastCode = cut.charCodeAt(cut.length - 1);

  // Don't end on half an astral character (an emoji, a rarer CJK glyph).
  return lastCode >= 0xd800 && lastCode <= 0xdbff ? cut.slice(0, -1) : cut;
};

/** Floor a playback position into its bucket, so ticks don't churn the envelope. */
const bucketedTimestamp = (
  timestampSec: number | null | undefined
): number | null => {
  if (typeof timestampSec !== 'number' || !Number.isFinite(timestampSec)) {
    return null;
  }

  const bucket = CHAT_VIDEO_TIMESTAMP_BUCKET_SECONDS;

  return Math.max(0, Math.floor(timestampSec / bucket) * bucket);
};

/**
 * The media length in whole seconds, or undefined when the player has not
 * reported a usable one (ORA-507). A native player reports 0 or NaN until its
 * metadata loads and an iframe embed never reports at all; neither is a length.
 */
const wholeDuration = (
  durationSec: number | null | undefined
): number | undefined => {
  if (typeof durationSec !== 'number' || !Number.isFinite(durationSec)) {
    return undefined;
  }

  const whole = Math.round(durationSec);

  return whole > 0 ? whole : undefined;
};

/**
 * The wire anchor.
 *
 * The visibility hint may only narrow, with one exception. It is set to
 * `hidden` when the surface says the content is still unrevealed, and forced
 * on for a live self-assessment. A question anchor never asserts a reveal —
 * the server derives that from the student's own submitted response, and a
 * client-asserted reveal would be a way to read the answer key out.
 *
 * A flipped flashcard is the exception (ORA-459): there is no submitted
 * response to derive anything from, so the surface saying `revealed` is the
 * only way the companion can know the back of the card is already on screen.
 * A live self-assessment still overrides it.
 */
const toAnchorRef = (
  anchor: ChatAnchorInput | null,
  selfAssessmentLive: boolean
): ChatAnchorRef | undefined => {
  if (!anchor) return undefined;

  const hidden = (revealed?: boolean) =>
    selfAssessmentLive || revealed === false
      ? ({ visibility: 'hidden' } as const)
      : undefined;

  /** Flashcards only; every other kind keeps `hidden`-or-nothing. */
  const flashcardVisibility = (revealed?: boolean) => {
    if (selfAssessmentLive || revealed === false) {
      return { visibility: 'hidden' } as const;
    }

    return revealed === true
      ? ({ visibility: 'revealed' } as const)
      : undefined;
  };

  switch (anchor.type) {
    case 'question':
      return {
        type: 'question',
        id: anchor.vignetteId,
        ...(anchor.variantId ? { variantId: anchor.variantId } : {}),
        ...hidden(anchor.revealed),
      };
    case 'flashcard':
      return {
        type: 'flashcard',
        id: anchor.flashcardId,
        ...(anchor.variantId ? { variantId: anchor.variantId } : {}),
        ...flashcardVisibility(anchor.revealed),
      };
    case 'video':
      return { type: 'video', id: anchor.videoId, ...hidden() };
    case 'article':
      return { type: 'article', id: anchor.articleId, ...hidden() };
    case 'lecture':
      return { type: 'lecture', id: anchor.lectureId, ...hidden() };
    case 'exam':
      return { type: 'exam', id: anchor.examId, ...hidden() };
  }
};

const qbankActivity = (
  input: Pick<AppContextEnvelopeInput, 'route' | 'study'>
): ChatActivity | undefined => {
  const { activeBlock: block, activeQuestionBlockId, phase } = input.study;

  if (!block) return undefined;

  // The route is authoritative for review: `StudyPhaseRouteSync` sets the phase
  // in an effect, so the first render after navigating to review still carries
  // the study phase, and a block whose submit response has not landed yet is
  // not marked submitted either. A self-assessment whose correctness stays
  // hidden after submission is never review, however it was reached.
  const mode =
    !isUnreviewableAttempt(block) &&
    (input.route === 'study.qbank.review' ||
      block.isSubmitted ||
      phase === StudyPhase.BLOCK_REVIEW)
      ? 'review'
      : 'study';

  // The index belongs to whichever block the vignettes were last loaded for;
  // while a new block is loading it still points into the old one.
  const isIndexForThisBlock =
    activeQuestionBlockId === null || activeQuestionBlockId === block.id;

  const index = isIndexForThisBlock
    ? Math.min(input.study.activeQuestionIndex + 1, block.vignetteCount)
    : 1;

  return {
    kind: 'qbank_block',
    blockId: block.id,
    mode,
    // An empty block has no first question; `index` is 1-based, so 0 would lie.
    ...(block.vignetteCount > 0 ? { index } : {}),
    total: block.vignetteCount,
  };
};

/**
 * The QBank block a drawer's thread may follow (ORA-1285): the block the study
 * route is showing, and where the question on screen sits in it. `anchor` must
 * be the *surface* anchor, never a block's claim, for the reason thread keys
 * follow it (ORA-628): a video served into the block chat must not move the
 * thread off the block.
 *
 * Null off the QBank study routes, for a surface anchor that is not a question,
 * for an empty block, and for a self-assessment block, which the server never
 * gives a block chat.
 */
export const deriveChatBlockContext = (
  input: Pick<AppContextEnvelopeInput, 'route' | 'anchor' | 'study'>
): ChatBlockContext | null => {
  if (
    input.route !== 'study.qbank.block' &&
    input.route !== 'study.qbank.review'
  )
    return null;
  if (input.anchor?.type !== 'question') return null;

  const block = input.study.activeBlock;
  if (!block || block.selfAssessmentAttemptId) return null;

  const activity = qbankActivity(input);
  if (activity?.kind !== 'qbank_block' || !activity.index) return null;

  return {
    blockId: activity.blockId,
    index: activity.index,
    total: activity.total,
  };
};

const flashcardActivity = (
  run: AppContextFlashcardRun | null
): ChatActivity | undefined => {
  const total = run?.totalCount ?? 0;

  // No run means no run: reporting an empty one would tell the companion the
  // student is mid-review when they are looking at a deck list.
  if (total <= 0) return undefined;

  return {
    kind: 'flashcard_run',
    scope: run?.scope ?? null,
    // A finished run has `completedCount === totalCount`, which must not read
    // as "on card 31 of 30".
    position: Math.min((run?.completedCount ?? 0) + 1, total),
    total,
  };
};

/**
 * One activity at a time. An anchor wins over the route, because the anchor is
 * the thing the student is actually looking at (a video anchor set from the
 * documents page still means "watching a video"); the route only decides when
 * no anchor says otherwise, and an anchor whose activity cannot be described
 * falls through to the route rather than inventing one.
 */
const resolveActivity = (input: AppContextEnvelopeInput): ChatActivity => {
  const { anchor, route, study } = input;

  // A course step runs its questions and cards outside the study slices, so
  // it publishes the activity those slices would have produced (handoff 2.2).
  // Videos and readings publish nothing here: their anchors carry it.
  if (route === 'course.session' && input.courseActivity) {
    return input.courseActivity;
  }

  switch (anchor?.type) {
    case 'lecture':
      return {
        kind: 'document',
        lectureId: anchor.lectureId,
        pages: anchor.pages ?? null,
      };
    case 'video': {
      const durationSec = wholeDuration(anchor.durationSec);

      return {
        kind: 'video',
        videoId: anchor.videoId,
        timestampSec: bucketedTimestamp(anchor.timestampSec),
        // Absent rather than null: the server's schema treats a missing length
        // as "unknown" and falls back to what it has stored.
        ...(durationSec !== undefined ? { durationSec } : {}),
        playing: anchor.playing ?? false,
      };
    }
    case 'article':
      return {
        kind: 'article',
        articleId: anchor.articleId,
        sectionId: anchor.sectionId ?? null,
      };
    case 'flashcard': {
      const activity = flashcardActivity(study.flashcardRun);

      if (activity) return activity;
      break;
    }
    // A question anchor is not always the study surface's own: a question
    // block the companion served into the thread claims the anchor too, on
    // any route. The block activity only describes the session the student
    // is standing in, so it is reported only from the QBank routes. Elsewhere
    // the study slice can still hold the LAST block — one left unsubmitted
    // hours ago — and reporting it told the server the student was mid-block
    // on the chat page, which scoped their answer to that block and kept the
    // key of a question they had just answered hidden (ORA-628).
    case 'question': {
      if (!QBANK_ROUTES.includes(route)) break;

      const activity = qbankActivity(input);

      if (activity) return activity;
      break;
    }
    default:
      break;
  }

  if (QBANK_ROUTES.includes(route)) {
    return qbankActivity(input) ?? { kind: 'idle' };
  }

  if (route === 'study.flashcards') {
    return flashcardActivity(study.flashcardRun) ?? { kind: 'idle' };
  }

  if (route === 'dashboard' && study.todaySessionId) {
    return { kind: 'today_session', sessionId: study.todaySessionId };
  }

  return { kind: 'idle' };
};

/** Build the envelope, and say whether chat may be shown at all. */
export const deriveAppContextEnvelope = (
  input: AppContextEnvelopeInput
): AppContextEnvelopeResult => {
  const selfAssessmentLive = isSelfAssessmentLive(
    input.study.activeBlock,
    input.route,
    input.study.activeQuestionBlockId
  );

  // Detach is matched on the key, not on the anchor object: a page pill has no
  // anchor at all, and a re-published anchor is a new object every render.
  const isDetached =
    input.detachedContextKey != null &&
    input.detachedContextKey === chatContextKey(input.anchor, input.route);

  // The anchor still goes out when the student detaches, with the flag beside
  // it. The server drops it from the prompt and never hydrates it, but it is
  // also what threads are keyed by: withholding it would drop the first
  // message of a detached question's thread into the generic one.
  const anchor = toAnchorRef(input.anchor, selfAssessmentLive);

  // Free text never leaves a live attempt: a stale selection captured earlier,
  // or a tooltip that escaped the gate, would otherwise carry exam prose to the
  // model.
  const selection = selfAssessmentLive
    ? undefined
    : capped(input.selection, CHAT_SELECTION_MAX_CHARS);

  // "Ask Ora AI" on a highlight seeds the composer with the selection itself
  // (ORA-1041). The selection already quotes the passage; sending it again as
  // the prefill would only repeat it to the model.
  const prefill =
    selfAssessmentLive || input.prefill === input.selection
      ? undefined
      : capped(input.prefill, CHAT_PREFILL_MAX_CHARS);

  // Removing a *page* pill drops everything the page was contributing, and the
  // activity is part of that: "on today's session" is exactly the kind of
  // ambient fact the student asked us to stop reading. A content pill keeps it
  // — the block, the run, the playback position describe where they are
  // standing, not the item they detached.
  const activity: ChatActivity =
    isDetached && !input.anchor ? { kind: 'idle' } : resolveActivity(input);

  return {
    envelope: {
      surface: input.surface,
      route: input.route,
      ...(input.route === 'help' && input.path ? { path: input.path } : {}),
      activity,
      ...(input.viewing ? { viewing: input.viewing } : {}),
      ...(input.course ? { course: input.course } : {}),
      ...(anchor ? { anchor } : {}),
      ...(isDetached ? { anchorDetached: true } : {}),
      ...(selection ? { selection } : {}),
      ...(prefill ? { prefill } : {}),
    },
    isChatAvailable: !selfAssessmentLive,
  };
};
